import { useEffect, useRef } from "react";

import { isEmpty } from "@king-ict/eupisi-ui/utils";
import type { History } from "history";
import { v4 as uuidv4 } from "uuid";

import { getInstitutionDetails } from "../../../api/kindergartenApi";
import { useTypedSelector } from "../../../app/rootReducer";
import { useAppDispatch } from "../../../app/store";
import { useCurrentUser } from "../../../hooks/authorization/useCurrentUser";
import { dashboard as dashboardRoute } from "../../../routes/routes";
import {
  getApplicantParentByOib,
  getChildByOib,
  setRequestUID,
  updateSelectedInstitution,
} from "../../../slices/requestSlice";
import {
  buildBootstrapRequestKey,
  getIsRequestCreationOpen,
  getShouldFetchChildRequestCount,
} from "./utils";

interface IParams {
  applicantParentOibValue?: string;
  childOibValue?: string;
  history: History;
  isNewRequestRoute: boolean;
  isPreschoolRequestRoute: boolean;
}

export const useRequestBootstrap = ({
  applicantParentOibValue,
  childOibValue,
  history,
  isNewRequestRoute,
  isPreschoolRequestRoute,
}: IParams) => {
  const dispatch = useAppDispatch();
  const user = useCurrentUser();
  const pedagogicalYear = useTypedSelector((state) => state.pedagogicalYear);
  const bootstrapRequestKeyRef = useRef<string | null>(null);

  const applicantOib = applicantParentOibValue ?? user.oib;
  const institutionId = user.institutionId;
  const currentImportantDate = pedagogicalYear.currentImportantDate;
  const currentPedagogicalYear = pedagogicalYear.currentPedagogicalYear;
  const isImportantDateReady = !isEmpty(currentImportantDate);
  const isPedagogicalYearReady = !isEmpty(currentPedagogicalYear);
  const isRequestCreationOpen = getIsRequestCreationOpen({
    currentImportantDateEventId: currentImportantDate.eventId,
    currentPedagogicalYearId: currentPedagogicalYear.id,
  });

  useEffect(() => {
    if (!isNewRequestRoute) {
      bootstrapRequestKeyRef.current = null;
      return;
    }

    if (
      !applicantOib ||
      !childOibValue ||
      !isImportantDateReady ||
      !isPedagogicalYearReady
    ) {
      return;
    }

    if (!isRequestCreationOpen) {
      history.push(dashboardRoute());
      return;
    }

    const bootstrapRequestKey = buildBootstrapRequestKey({
      applicantOib,
      childOibValue,
      eventId: currentImportantDate.eventId,
      institutionId,
      isPreschoolRequestRoute,
      pedagogicalYearId: currentPedagogicalYear.id,
    });

    if (bootstrapRequestKeyRef.current === bootstrapRequestKey) {
      return;
    }

    bootstrapRequestKeyRef.current = bootstrapRequestKey;

    dispatch(setRequestUID(uuidv4()));
    dispatch(getApplicantParentByOib(applicantParentOibValue));
    dispatch(getChildByOib(childOibValue));

    if (institutionId) {
      getInstitutionDetails(+institutionId).then((res) => {
        dispatch(updateSelectedInstitution(res));
      });
    }
  }, [
    applicantOib,
    applicantParentOibValue,
    childOibValue,
    currentImportantDate.eventId,
    currentPedagogicalYear.id,
    dispatch,
    history,
    institutionId,
    isImportantDateReady,
    isNewRequestRoute,
    isPedagogicalYearReady,
    isPreschoolRequestRoute,
    isRequestCreationOpen,
  ]);

  return {
    shouldFetchChildRequestCount: getShouldFetchChildRequestCount({
      applicantOib,
      childOibValue,
      isImportantDateReady,
      isNewRequestRoute,
      isPedagogicalYearReady,
      isRequestCreationOpen,
    }),
  };
};
